import { z } from 'zod';

import { dateTimeOffsetSchema, dateTimeSchema } from '@apple/utils/globalization/models';
import type { PaginationResult } from '@apple/utils/pagination/models';

export const queueMessageIdSchema = z.guid();
export const queueTypeSchema = z.string().min(1);
export const queueStatusSchema = z.enum([
	'Queued',
	'Scheduled',
	'Failed',
	'Abandoned',
	'Completed',
]);

export const messageStatusSchema = z.enum(['Queued', 'Failed', 'Abandoned', 'Completed']);

export type QueueMessageId = z.infer<typeof queueMessageIdSchema>;
export type QueueType = z.infer<typeof queueTypeSchema>;
export type QueueStatus = z.infer<typeof queueStatusSchema>;
export type MessageStatus = z.infer<typeof messageStatusSchema>;
export type QueueFilters = z.infer<typeof queueFiltersSchema>;

export interface Queue {
	queue: QueueType;
	queuedCount: number;
	scheduledCount: number;
	failedCount: number;
	abandonedCount: number;
	completedCount: number;
}

export interface QueueMessageListing {
	messageId: QueueMessageId;
	queue: QueueType;
	status: MessageStatus;
	enqueuedOn: Date;
	enqueueAfter: Date | null;
	lockedUntil: Date | null;
	deliveryCount: number;
	maxDeliveryCount: number;
	lastAttemptedOn: Date | null;
	completedOn: Date | null;
	abandonedOn: Date | null;
}

export interface QueueMessage extends QueueMessageListing {
	body: string;
	rowVersion: string | null;
	canBeRequeued: boolean;
	canBeAbandoned: boolean;
}

export interface QueueItemLogModel {
	timestamp: Date;
	severity: number;
	attempt: number;
	title: string;
	message: string;
	errors: string | null;
}
export interface QueueRequest {
	currentPage?: number;
	pageSize?: number;
	filters?: QueueFilters;
}

export const queueSchema = z.object({
	queue: queueTypeSchema,
	queuedCount: z.number(),
	scheduledCount: z.number(),
	failedCount: z.number(),
	abandonedCount: z.number(),
	completedCount: z.number(),
}) satisfies z.ZodType<Queue>;

export const queueMessageListingSchema = z.object({
	messageId: queueMessageIdSchema,
	queue: queueTypeSchema,
	status: messageStatusSchema,
	enqueuedOn: dateTimeSchema,
	enqueueAfter: dateTimeSchema.nullable(),
	lockedUntil: dateTimeSchema.nullable(),
	deliveryCount: z.number(),
	maxDeliveryCount: z.number(),
	lastAttemptedOn: dateTimeSchema.nullable(),
	completedOn: dateTimeSchema.nullable(),
	abandonedOn: dateTimeSchema.nullable(),
}) satisfies z.ZodType<QueueMessageListing>;

export const queueMessageSchema = queueMessageListingSchema.extend({
	body: z.string(),
	rowVersion: z.string().nullable(),
	canBeRequeued: z.boolean(),
	canBeAbandoned: z.boolean(),
}) satisfies z.ZodType<QueueMessage>;

export const queueMessagePaginationResultSchema = z.object({
	currentPage: z.number().int().nonnegative(),
	pageSize: z.number().int().nonnegative(),
	totalCount: z.number().int().nonnegative(),
	results: z.array(queueMessageListingSchema),
}) satisfies z.ZodType<PaginationResult<QueueMessageListing>>;

export const queueItemLogSchema = z.object({
	timestamp: dateTimeOffsetSchema,
	severity: z.number(),
	attempt: z.number(),
	title: z.string(),
	message: z.string(),
	errors: z.string().nullable(),
}) satisfies z.ZodType<QueueItemLogModel>;

export const queueFiltersSchema = z.object({
	queue: z.string().optional(),
	lastAttemptOn: z.string().optional(),
	completedOn: z.string().optional(),
});
